import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { PHONE_DISPLAY, PHONE_RAW, CALENDLY_URL } from "@/lib/api";

const NAV = [
  {
    to: "/",
    label: "Home",
    children: [
      { to: "/our-team", label: "Our Team" },
      { to: "/contact", label: "Contact Us" },
    ],
  },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/bookkeeping-services", label: "Bookkeeping" },
  { to: "/virtual-assistant-services", label: "Virtual Assistance" },
  { to: "/marketing-assistance", label: "Marketing Assistance" },
  { to: "/customer-support", label: "Customer Support" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleEnter = (idx) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setOpenDropdown(idx);
  };
  const handleLeave = () => {
    dropdownTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream-100/85 backdrop-blur-md border-b border-forest-500/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/4p3jd3rm_Logo-removebg-preview.png"
            alt="aidify logo"
            className="h-20 md:h-24 w-auto object-contain"
            style={{ filter: 'brightness(0.55) saturate(1.8) contrast(1.1)' }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Primary">
          {NAV.map((n, i) =>
            n.children ? (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={() => handleEnter(i)}
                onMouseLeave={handleLeave}
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-[13.5px] font-medium whitespace-nowrap transition-colors ${
                      isActive ? "text-forest-600" : "text-forest-400 hover:text-forest-600"
                    }`
                  }
                >
                  {n.label} <ChevronDown size={14} className="opacity-70" />
                </NavLink>

                {openDropdown === i && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 min-w-[180px]"
                    data-testid="nav-home-dropdown"
                  >
                    <div className="aidify-card py-2 shadow-lg">
                      {n.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-dropdown-${c.label.toLowerCase().replace(/ /g, "-")}`}
                          className={({ isActive }) =>
                            `block px-4 py-2 text-sm transition-colors ${
                              isActive
                                ? "text-forest-600 bg-sage-100/60"
                                : "text-forest-500 hover:text-forest-600 hover:bg-cream-200/60"
                            }`
                          }
                          onClick={() => setOpenDropdown(null)}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `text-[13.5px] font-medium whitespace-nowrap transition-colors ${
                    isActive ? "text-forest-600" : "text-forest-400 hover:text-forest-600"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <a
            href={`tel:${PHONE_RAW}`}
            data-testid="header-phone"
            className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-forest-500/15 hover:border-forest-500/40 transition-colors text-sm font-medium text-forest-600 whitespace-nowrap"
          >
            <Phone size={14} /> {PHONE_DISPLAY}
          </a>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="header-cta" className="btn-primary text-sm whitespace-nowrap">
            Book a Call →
          </a>
        </div>

        <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="header-cta-compact" className="hidden lg:inline-flex xl:hidden btn-primary text-sm whitespace-nowrap shrink-0">
          Book a Call →
        </a>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-full border border-forest-500/15"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-forest-500/10 bg-cream-50" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV.map((n) => (
              <div key={n.to}>
                <NavLink
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block py-2 text-base font-medium ${isActive ? "text-forest-600" : "text-forest-400"}`
                  }
                >
                  {n.label}
                </NavLink>
                {n.children && (
                  <div className="pl-4 border-l border-forest-500/10 ml-1">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `block py-1.5 text-sm ${isActive ? "text-forest-600" : "text-forest-400/80"}`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="btn-primary mt-2 justify-center">
              Book a Call →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
